package com.evoapps.evoshift.widgets

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.FlowRow
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.foundation.text.KeyboardActions
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.AddCircle
import androidx.compose.material.icons.filled.Close
import androidx.compose.material3.Icon
import androidx.compose.material3.InputChip
import androidx.compose.material3.InputChipDefaults
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.text.input.ImeAction
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.ui.DarkGold
import com.evoapps.evoshift.ui.adaptive.handCursor
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.absences_remove
import evoshift.composeapp.generated.resources.add
import org.jetbrains.compose.resources.stringResource

/**
 * Edits a list of short names (roles, skills) inside a navy dialog: type and press Enter or
 * the add button; each entry is a chip with a remove button. Duplicates are ignored.
 */
@Composable
fun StringListEditor(
    title: String,
    items: List<String>,
    onAdd: (String) -> Unit,
    onRemove: (String) -> Unit,
    hint: String
) {
    var newItem by remember { mutableStateOf("") }

    fun add() {
        val item = newItem.trim()
        if (item.isNotEmpty() && items.none { it.equals(item, ignoreCase = true) }) onAdd(item)
        newItem = ""
    }

    DialogSection(title) {
        Row(verticalAlignment = Alignment.CenterVertically) {
            EvoTextField(
                value = newItem,
                onValueChange = { newItem = it },
                label = hint,
                imeAction = ImeAction.Done,
                keyboardActions = KeyboardActions(onDone = { add() }),
                modifier = Modifier.weight(1f)
            )
            IconButtonWithTooltip(onClick = { add() }, enabled = newItem.isNotBlank(), modifier = Modifier.handCursor(), tooltip = stringResource(Res.string.add)) {
                Icon(
                    imageVector = Icons.Default.AddCircle,
                    contentDescription = stringResource(Res.string.add),
                    tint = if (newItem.isNotBlank()) DarkGold else Color.White.copy(alpha = 0.3f)
                )
            }
        }

        if (items.isNotEmpty()) {
            FlowRow(
                modifier = Modifier.fillMaxWidth(),
                horizontalArrangement = Arrangement.spacedBy(8.dp),
                verticalArrangement = Arrangement.spacedBy(4.dp)
            ) {
                items.forEach { item ->
                    InputChip(
                        selected = false,
                        onClick = { onRemove(item) },
                        label = { Text(item) },
                        trailingIcon = {
                            Icon(
                                Icons.Default.Close,
                                contentDescription = stringResource(Res.string.absences_remove),
                                modifier = Modifier.size(16.dp)
                            )
                        },
                        shape = RoundedCornerShape(10.dp),
                        colors = InputChipDefaults.inputChipColors(
                            labelColor = Color.White,
                            trailingIconColor = Color.White.copy(alpha = 0.7f),
                            containerColor = Color.White.copy(alpha = 0.08f)
                        ),
                        border = InputChipDefaults.inputChipBorder(
                            enabled = true,
                            selected = false,
                            borderColor = DarkGold.copy(alpha = 0.4f)
                        ),
                        modifier = Modifier.handCursor()
                    )
                }
            }
        }
    }
}
